// ARRANGEMENT PLANNER — the seam between the dialog's screen state and the BACKEND decomposer.
//
// CR-20260830-002 MOVED THE DECOMPOSITION TO THE BACKEND. The engine that lived in
// `optimal-arrangement.ts` (2413 lines) is retired; its logical-bit-identical C# port runs in
// `ETS.ShellService.Shell/OrderArrangement`, built from the owner's reference script
// `decompose_legs.py` — which is now the authoritative law. THE CANDIDATE MACHINERY IS GONE WITH
// IT (owner ruling, 2026-08-30): no baseline, no price-effectiveness contest, no "improve only
// the bad cards". The engine runs every time and returns up to two ranked covers; the USER
// compares them against the screen. The only remnant of the old baseline is rule A, judged on the
// backend: a best cover IDENTICAL to the current grouping comes back as an empty "Unchanged".
//
// WHAT THIS FILE STILL OWNS is pure translation:
//   screen -> wire : `buildOoaRequest` — groups to per-expiration piles (netted target legs +
//                    the RAW current grouping fragments rule A compares against);
//   wire -> screen : `composePlanPages` — reply solutions back to `PlannedTicket`s whose legs
//                    reference source wrappers, via `allocate`; netting cancels settled; naked
//                    residue composed into ONE ticket of its own (owner ruling) and the page
//                    marked DEGRADED (red replaces orange).
//
// ⚠⚠ CALLS AND PUTS SHARE ONE PILE (owner, 2026-08-26) — the right is part of each CONTRACT'S
// identity inside the pile, never a grouping key. EXPIRATION STILL SEPARATES: one pile per
// expiration, holding both rights. POOLING MAKES NETTING REAL: two groups holding the same
// contract on opposite sides net before the wire; the cancelled halves are settled on apply.

import {OrdersGroupToRegroup} from './OrdersGroupToRegroup';
import {OrderToRegroupWrapper} from './OrderToRegroupWrapper';
import {canonicalizePiles, OoaClientService, ooaRequestKey} from './ooa-client.service';
import {
    GetOptimalOrderArrangementReply,
    OoaLegDto,
    OoaPileDto,
    OoaPileResultDto,
    OoaSolutionDto,
    OoaTicketDto
} from '../shell-communication/ooa-operations';

export type Right = 'Call' | 'Put';

/** Fixed order, so the same pile always renders the same way. Calls first, as everywhere else. */
export const RIGHTS: Right[] = ['Call', 'Put'];

/** The netted pile: per right, strike -> net quantity. */
export interface Residual {
    Call: Map<number, number>;
    Put: Map<number, number>;
}

export function emptyResidual(): Residual {
    return {Call: new Map<number, number>(), Put: new Map<number, number>()};
}

/** The pile's contract identity. Must match the backend's netting key exactly. */
function contractKey(right: Right, strike: number): string {
    return `${right}|${strike}`;
}

export interface PlannedLeg {
    wrapper: OrderToRegroupWrapper;
    /** Signed quantity this ticket takes from that source row. */
    qty: number;
}

export interface PlannedTicket {
    legs: PlannedLeg[];
    /** The backend's kind (condor, iron-condor, …), 'as-sent' for kept fragments, 'naked' for residue. */
    shape: string;
}

export interface ArrangementPlan {
    tickets: PlannedTicket[];
    /** Rows that net out against each other and need no ticket. Settled, never shipped. */
    cancelled: PlannedLeg[];
    /** Everything the user must be told: naked residue, skipped piles, backend notes. */
    warnings: string[];
    /** True when any pile's applied solution carries naked residue — RED replaces orange. */
    degraded: boolean;
    /**
     * True for a torn-variant page (multipliers tear the quantity blocks; huge win only).
     * Pages carry NO label (owner ruling 2026-09-03: the pager alone - "1 of 2" - names them).
     */
    torn: boolean;
}

/**
 * The whole answer of one backend run: up to two ranked plans ("pages" — the pager slides
 * between them), or the verdict that there is nothing to offer.
 *
 *   'ok'        — pages hold 1..2 applicable plans, best first.
 *   'unchanged' — every pile came back Unchanged/Unresolved with nothing to apply; the screen
 *                 stays as it is (no glow, no offer). Warnings may still explain why.
 *   'error'     — transport/backend failure or give-up timeout; proceed un-optimized, warn.
 */
export interface ArrangementPlanPages {
    status: 'ok' | 'unchanged' | 'error';
    pages: ArrangementPlan[];
    warnings: string[];
    requestId?: string;
    /** Canonical request key — lets a consumer verify a passed-through result is still fresh. */
    requestKey: string;
}

/**
 * Run the optimal arrangement against the backend and compose its reply into applicable plans.
 * Pure toward the screen: no source row is settled here — settling happens only when the dialog
 * constructs `DropZoneOrder`s from a plan.
 */
export async function planOptimalArrangement(callSide: OrdersGroupToRegroup[],
                                             putSide: OrdersGroupToRegroup[],
                                             ooa: OoaClientService,
                                             atm: number | null): Promise<ArrangementPlanPages> {

    const bundle = buildOoaRequest(callSide, putSide);
    const requestKey = ooaRequestKey(canonicalizePiles(bundle.piles), atm);

    if (bundle.piles.length === 0) {
        return {
            status: 'unchanged',
            pages: [],
            warnings: ['nothing to arrange - no priced option legs on screen.'],
            requestKey
        };
    }

    let reply: GetOptimalOrderArrangementReply;

    try {
        reply = await ooa.request(bundle.piles, atm);
    } catch (e) {
        return {
            status: 'error',
            pages: [],
            warnings: ['Optimal arrangement is unavailable right now - the orders are shown as received.'],
            requestKey
        };
    }

    const composed = composePlanPages(reply, bundle);
    composed.requestId = reply.requestId;
    composed.requestKey = requestKey;

    return composed;
}

/** The canonical key for the CURRENT screen — for freshness checks on passed-through results. */
export function arrangementRequestKey(callSide: OrdersGroupToRegroup[],
                                      putSide: OrdersGroupToRegroup[],
                                      atm: number | null): string {
    return ooaRequestKey(canonicalizePiles(buildOoaRequest(callSide, putSide).piles), atm);
}

// ---------------------------------------------------------------------------------------------
// screen -> wire
// ---------------------------------------------------------------------------------------------

interface PreparedPile {
    expiration: string;
    netting: Netting;
    /** The RAW screen cards' rows for this expiration, one PlannedTicket per card — the as-sent
     *  fragments an Unchanged/Unresolved pile keeps on every page. */
    fragments: PlannedTicket[];
}

export interface OoaRequestBundle {
    piles: OoaPileDto[];
    prepared: PreparedPile[];
}

export function buildOoaRequest(callSide: OrdersGroupToRegroup[],
                                putSide: OrdersGroupToRegroup[]): OoaRequestBundle {

    const groups = (callSide || []).concat(putSide || []);
    const wrappers = collectWrappers(groups);

    // Earlier expirations first — a fixed order, so the same screen always produces the same
    // request (and the same backend log echo).
    const ladders = buildLadders(wrappers);

    const piles: OoaPileDto[] = [];
    const prepared: PreparedPile[] = [];

    ladders.forEach(ladder => {

        const netting = netContracts(ladder);

        const legs: OoaLegDto[] = [];

        RIGHTS.forEach(right => {
            netting.target[right].forEach((net, strike) => {
                legs.push({right, strike, signedQty: net});
            });
        });

        const currentGrouping: OoaTicketDto[] = [];
        const fragments: PlannedTicket[] = [];

        groups.forEach(group => {

            const rows = (group.orders || []).filter(r =>
                r.qty && r.expiration === ladder.expiration && isFinite(parseFloat(r.strike)));

            if (rows.length === 0) {
                return;
            }

            currentGrouping.push({
                legs: rows.map(r => ({
                    right: r.type,
                    strike: parseFloat(r.strike),
                    signedQty: r.qty
                }))
            });

            fragments.push({
                legs: rows.map(r => ({wrapper: r, qty: r.qty})),
                shape: 'as-sent'
            });
        });

        piles.push({expiration: ladder.expiration, legs, currentGrouping});
        prepared.push({expiration: ladder.expiration, netting, fragments});
    });

    return {piles, prepared};
}

// ---------------------------------------------------------------------------------------------
// wire -> screen
// ---------------------------------------------------------------------------------------------

/** One pile's solutions in delivery order: solution one, solution two (when one qualifies), torn. */
interface TeamSplit {
    /** Solution one - the first delivered solution, the best. */
    first: OoaSolutionDto;
    /** Solution two - the next best (= `first` when the pile delivered a single solution). */
    second: OoaSolutionDto;
    /** True when the pile delivered a single solution. */
    single: boolean;
    torn: OoaSolutionDto[];
}

function splitTeams(solutions: OoaSolutionDto[]): TeamSplit | null {
    const winners = solutions.filter(s => !s.torn);

    if (winners.length === 0) {
        return null;
    }

    return {
        first: winners[0],
        second: winners.length > 1 ? winners[1] : winners[0],
        single: winners.length === 1,
        torn: solutions.filter(s => s.torn)
    };
}

/**
 * Compose the reply into plan pages: page 0 applies each pile's solution one, page 1 its
 * solution two (absent when no pile delivered a second solution; a single-solution pile
 * contributes that solution to both pages), and any TORN VARIANT pages follow, flagged. A pile
 * with nothing to apply (Unchanged, Unresolved, or an allocation refusal) keeps its as-sent
 * fragments on every page. Warnings are carried onto every page they concern; naked residue
 * becomes one extra ticket and marks the page degraded.
 */
export function composePlanPages(reply: GetOptimalOrderArrangementReply,
                                 bundle: OoaRequestBundle): ArrangementPlanPages {

    const byExpiration = new Map<string, OoaPileResultDto>();
    (reply.piles || []).forEach(pile => byExpiration.set(pile.expiration, pile));

    const teamsByExpiration = new Map<string, TeamSplit>();
    let anyTwoWinners = false;
    let tornPageCount = 0;
    const allWarnings: string[] = [];

    bundle.prepared.forEach(prepared => {
        const result = byExpiration.get(prepared.expiration);

        if (!result) {
            return;
        }

        result.warnings.forEach(w => allWarnings.push(`${prepared.expiration}: ${w}`));

        const teams = splitTeams(result.solutions || []);

        if (teams) {
            teamsByExpiration.set(prepared.expiration, teams);
            anyTwoWinners = anyTwoWinners || !teams.single;
            tornPageCount = Math.max(tornPageCount, teams.torn.length);
        }
    });

    if (teamsByExpiration.size === 0) {
        return {status: 'unchanged', pages: [], warnings: allWarnings, requestKey: ''};
    }

    const winnersPageCount = anyTwoWinners ? 2 : 1;
    const pages: ArrangementPlan[] = [];

    for (let k = 0; k < winnersPageCount + tornPageCount; k++) {

        const tornIx = k - winnersPageCount;

        const page: ArrangementPlan = {
            tickets: [], cancelled: [], warnings: [], degraded: false, torn: tornIx >= 0
        };

        bundle.prepared.forEach(prepared => {

            const result = byExpiration.get(prepared.expiration);
            const teams = teamsByExpiration.get(prepared.expiration);

            if (!teams) {
                // Unchanged / Degraded / Unresolved / missing: this pile keeps its screen cards as
                // they are. Degraded (owner ruling 2026-09-02): the backend REJECTED every
                // packaging because it leaves a leg naked - nothing offered; its warnings carry the
                // reason and must reach the user like a time-out's do.
                page.tickets = page.tickets.concat(prepared.fragments);

                if (result && (result.status === 'Unresolved' || result.status === 'Degraded')) {
                    result.warnings.forEach(w => page.warnings.push(`${prepared.expiration}: ${w}`));
                }

                return;
            }

            // Solution pages: 0 = solution one, 1 = solution two. Torn pages: the pile's k-th torn
            // variant, or its solution two when it has none at that index.
            let solution: OoaSolutionDto;

            if (tornIx < 0) {
                solution = k === 0 ? teams.first : teams.second;
            } else if (tornIx < teams.torn.length) {
                solution = teams.torn[tornIx];
            } else {
                solution = teams.second;
            }

            const allocated = allocateSolution(solution, prepared.netting);

            if (!allocated) {
                // Half-allocated tickets are worse than none: the pile falls back whole.
                console.error(`[arrange] ${prepared.expiration}: solution legs could not be ` +
                    `matched back to the orders on screen - pile left as received`);
                page.tickets = page.tickets.concat(prepared.fragments);
                page.warnings.push(`${prepared.expiration}: skipped - its legs could not be ` +
                    `matched back to the orders on screen.`);
                return;
            }

            page.tickets = page.tickets.concat(allocated);
            page.cancelled = page.cancelled.concat(prepared.netting.cancelled);

            if (solution.naked.length > 0) {
                // Owner 2026-09-09: a naked pile raises ONE warning - this line. The backend's
                // diagnostic notes for the pile (structure mismatch, provable naked units) say the
                // same thing in engine words and are dropped here, not shown as extra toasts.
                page.degraded = true;
                page.warnings.push(`${prepared.expiration}: ${solution.naked.length} leg(s) cannot ` +
                    `close into any shape and ship as a naked order: ` +
                    solution.naked.map(describeLeg).join(', ') + '.');
            } else {
                result.warnings.forEach(w => page.warnings.push(`${prepared.expiration}: ${w}`));
            }
        });

        pages.push(page);
    }

    return {status: 'ok', pages, warnings: allWarnings, requestKey: ''};
}

/** Solution packages + naked residue -> allocatable tickets (naked = one ticket of its own). */
function allocateSolution(solution: OoaSolutionDto, netting: Netting): PlannedTicket[] | undefined {

    const tickets: Array<{ legs: Array<{ right: Right, strike: number, qty: number }>, shape: string }> = [];

    solution.packages.forEach(pkg => {
        tickets.push({
            legs: pkg.legs.map(l => ({right: l.right as Right, strike: l.strike, qty: l.signedQty})),
            shape: pkg.kind
        });
    });

    if (solution.naked.length > 0) {
        tickets.push({
            legs: solution.naked.map(l => ({right: l.right as Right, strike: l.strike, qty: l.signedQty})),
            shape: 'naked'
        });
    }

    return allocate(tickets, netting.available);
}

function describeLeg(leg: OoaLegDto): string {
    return `${leg.signedQty > 0 ? '+' : ''}${leg.signedQty} ${leg.right} ${leg.strike}`;
}

// ---------------------------------------------------------------------------------------------
// Ladders + netting + allocation — unchanged pure translation (CR-20260826/27 lineage).
// ---------------------------------------------------------------------------------------------

/**
 * ONE EXPIRATION, BOTH RIGHTS. Named "ladder" still, but it is the whole pile for that week:
 * the calls and the puts side by side, which is what lets an iron condor be found at all.
 */
interface Ladder {
    label: string;
    expiration: string;
    /**
     * ⚠ KEYED ON THE CONTRACT (`Call|7675`), NOT THE STRIKE. Keyed on the strike alone, a Call row
     * and a Put row on 7675 land in the same bucket and the netting sums them - netting a call
     * against a put, which is not netting at all. Two different contracts, two different buckets.
     */
    rows: Map<string, { right: Right, strike: number, wrappers: OrderToRegroupWrapper[] }>;
}

function buildLadders(wrappers: OrderToRegroupWrapper[]): Ladder[] {

    const byKey = new Map<string, Ladder>();

    wrappers.forEach(wrapper => {

        const strike = parseFloat(wrapper.strike);

        if (!isFinite(strike) || !wrapper.qty) {
            return;
        }

        // ⚠ THE RIGHT IS GONE FROM THIS KEY (owner, 2026-08-26). It used to read
        // `${wrapper.type}|${wrapper.expiration}`, which is the single line that kept Calls and Puts
        // in separate piles. Expiration alone now; the right lives on the contract instead.
        const key = wrapper.expiration;

        let ladder = byKey.get(key);

        if (!ladder) {
            ladder = {
                label: `${wrapper.expiration}`,
                expiration: wrapper.expiration,
                rows: new Map<string, { right: Right, strike: number, wrappers: OrderToRegroupWrapper[] }>()
            };
            byKey.set(key, ladder);
        }

        const ck = contractKey(wrapper.type, strike);
        const atContract = ladder.rows.get(ck);

        if (atContract) {
            atContract.wrappers.push(wrapper);
        } else {
            ladder.rows.set(ck, {right: wrapper.type, strike, wrappers: [wrapper]});
        }
    });

    const out: Ladder[] = [];
    byKey.forEach(ladder => out.push(ladder));

    return out.sort((a, b) => a.expiration.localeCompare(b.expiration));
}

interface Netting {
    /** The pile itself: per right, strike -> net quantity. Fed straight to the wire. */
    target: Residual;
    /** contract key -> the SINGLE source every ticket leg on that contract draws from. */
    available: Map<string, PlannedLeg>;
    /** Rows consumed by cancellation rather than by a ticket. */
    cancelled: PlannedLeg[];
}

/**
 * One source row standing for several that hold the same contract.
 *
 * WHY THIS EXISTS. An order ticket may hold at most one leg per contract - the dialog enforces
 * it on drag ("Selected leg already added to this order") and again at copy time. But pooling
 * makes two groups holding the same contract routine, and a ticket leg wanting 30 where the rows
 * hold 20 and 10 cannot be split across both without putting that contract in the ticket twice.
 * So the rows are netted into one source here, and settling it fans back out to the real rows -
 * which is what keeps the dialog's remaining-quantity display and its copy-time check honest.
 */
class MergedSource extends OrderToRegroupWrapper {

    constructor(private readonly parts: PlannedLeg[], netQty: number) {
        super(syntheticOrder(parts[0].wrapper, netQty), parts[0].wrapper.color);
    }

    adjustSettledQty(qty: number): boolean {

        if (!super.adjustSettledQty(qty)) {
            return false;
        }

        // Fan the settlement out in order, so each underlying row's own remaining quantity - the
        // "10 / 30" the user reads in the source column - stays truthful.
        let owed = qty;

        for (const part of this.parts) {

            if (owed === 0) {
                break;
            }

            const room = Math.sign(owed) === Math.sign(part.qty)
                ? Math.min(Math.abs(owed), Math.abs(part.wrapper.leftOverQty))
                : Math.min(Math.abs(owed), Math.abs(part.wrapper.settledQty));

            if (room === 0) {
                continue;
            }

            part.wrapper.adjustSettledQty(room * Math.sign(owed));
            owed -= room * Math.sign(owed);
        }

        return true;
    }
}

function syntheticOrder(model: OrderToRegroupWrapper, netQty: number): any {
    return {
        ticker: model.ticker,
        action: netQty > 0 ? 'Buy To Open' : 'Sell To Open',
        underlying: model.underlying,
        asset: model.underlying,
        type: model.type,
        strike: model.strike,
        expiration: model.expiration,
        role: model.role,
        price: model.price,
        qty: netQty
    };
}

/**
 * Net each CONTRACT'S rows down to one signed quantity, recording which rows cancelled and how much
 * of each remains for the tickets to draw on.
 *
 * ⚠⚠ PER CONTRACT, NEVER PER STRIKE (2026-08-26). `ladder.rows` is keyed on `Call|7675`, so a Call
 * row and a Put row on the same strike are never in the same bucket and can never net against each
 * other. Keyed on the strike alone this function would silently annihilate `Call +10 @7700` against
 * `Put -10 @7700` - two real, opposite positions retired against each other.
 */
function netContracts(ladder: Ladder): Netting {

    const target = emptyResidual();
    const available = new Map<string, PlannedLeg>();
    const cancelled: PlannedLeg[] = [];

    ladder.rows.forEach((bucket, ck) => {

        const rows = bucket.wrappers;

        const net = rows.reduce((acc, r) => acc + r.qty, 0);

        if (net === 0) {
            // The whole contract cancels: every row here is accounted for and none is traded.
            rows.forEach(r => cancelled.push({wrapper: r, qty: r.qty}));
            return;
        }

        const majority = Math.sign(net);

        // Rows on the losing side cancel completely; the winning side gives up the same amount.
        let toCancel = 0;

        rows.forEach(r => {
            if (Math.sign(r.qty) !== majority) {
                cancelled.push({wrapper: r, qty: r.qty});
                toCancel += Math.abs(r.qty);
            }
        });

        const remaining: PlannedLeg[] = [];

        rows.forEach(r => {

            if (Math.sign(r.qty) !== majority) {
                return;
            }

            let left = Math.abs(r.qty);

            if (toCancel > 0) {
                const take = Math.min(toCancel, left);
                cancelled.push({wrapper: r, qty: take * majority});
                toCancel -= take;
                left -= take;
            }

            if (left > 0) {
                remaining.push({wrapper: r, qty: left * majority});
            }
        });

        if (remaining.length === 0) {
            return;
        }

        target[bucket.right].set(bucket.strike, net);

        // One source per contract. A single surviving row is used as-is, so the common case
        // behaves exactly as a hand-dragged leg does.
        //
        // ⚠ EVERY ROW IN THIS BUCKET SHARES A CONTRACT, so `MergedSource` taking its model from
        // `parts[0]` copies a right that is already correct for all of them.
        available.set(ck, remaining.length === 1
            ? remaining[0]
            : {wrapper: new MergedSource(remaining, net), qty: net});
    });

    return {target, available, cancelled};
}

/**
 * Map each computed ticket leg back onto the source it must come from, and refuse the whole
 * pile if the pooled arithmetic and the rows ever disagree. Half-allocated tickets are worse
 * than none: they look complete on screen while reconstructing something other than the target.
 */
function allocate(tickets: Array<{ legs: Array<{ right: Right, strike: number, qty: number }>, shape: string }>,
                  available: Map<string, PlannedLeg>): PlannedTicket[] | undefined {

    // Track remaining amounts on a private copy, so a refusal leaves no partial state behind.
    const left = new Map<string, number>();
    available.forEach((leg, ck) => left.set(ck, leg.qty));

    const out: PlannedTicket[] = [];

    for (const ticket of tickets) {

        const legs: PlannedLeg[] = [];

        for (const leg of ticket.legs) {

            // ⚠ THE CONTRACT, NOT THE STRIKE. An iron butterfly asks for `Call -10 @7675` and
            // `Put -10 @7675` in one ticket; keyed on 7675 alone the second lookup would draw from
            // the first one's source and the leftover check would still balance.
            const ck = contractKey(leg.right, leg.strike);

            const source = available.get(ck);
            const remaining = left.get(ck);

            if (!source || remaining === undefined) {
                return undefined;
            }

            if (Math.sign(leg.qty) !== Math.sign(remaining) || Math.abs(leg.qty) > Math.abs(remaining)) {
                return undefined;
            }

            legs.push({wrapper: source.wrapper, qty: leg.qty});
            left.set(ck, remaining - leg.qty);
        }

        out.push({legs, shape: ticket.shape});
    }

    let leftover = 0;
    left.forEach(qty => leftover += Math.abs(qty));

    if (leftover !== 0) {
        return undefined; // the tickets did not consume the target exactly
    }

    return out;
}

function collectWrappers(groups: OrdersGroupToRegroup[]): OrderToRegroupWrapper[] {
    if (!groups) {
        return [];
    }
    return groups.reduce((acc, g) => acc.concat(g.orders || []), [] as OrderToRegroupWrapper[]);
}
